import { Component, Element, Event, EventEmitter, JSX, Prop, h } from '@stencil/core'
import { Validation } from '../../types/status.types'

@Component({
  tag: 'air-toggle',
  styleUrl: 'air-toggle.scss',
  shadow: true,
})
export class AirToggle {
  /** Reference to the host element */
  @Element() host!: HTMLAirToggleElement

  /** Defines name */
  @Prop({ reflect: true }) name?: string

  /** Specifies toggle label */
  @Prop() label?: string

  /** Specifies if toggle is required */
  @Prop() required = false

  /** Specifies if toggle is disabled */
  @Prop() disabled = false

  /** Specifies value */
  @Prop({ mutable: true }) value: boolean | undefined

  /** Specifies yes label */
  @Prop() trueLabel = 'Yes'

  /** Specifies no label */
  @Prop() falseLabel = 'No'

  /** Specifies validation of toggle */
  @Prop() validation?: Validation

  /** Triggers when an toggle value was changed and blur */
  @Event({ eventName: 'air-change' }) change!: EventEmitter<boolean>

  /** Triggers when an toggle in focus */
  @Event({ eventName: 'air-focus' }) focus!: EventEmitter<boolean>

  /** Triggers when an toggle has lost focus */
  @Event({ eventName: 'air-blur' }) blur!: EventEmitter<boolean>

  private setTrue = (): void => this.setValue(true)
  private setFalse = (): void => this.setValue(false)

  private setValue = (value: boolean): void => {
    this.value = value
    this.change.emit(this.value)
  }

  private handleFocus = (): void => {
    if (this.disabled) return
    this.focus.emit(this.value)
  }

  private handleBlur = (): void => {
    this.blur.emit(this.value)
  }

  private renderLabel(): HTMLLabelElement | null {
    return (
      this.label !== undefined && (
        <label class="toggle-label">
          {this.label}
          {this.required && <span class="required-mark">*</span>}
        </label>
      )
    )
  }

  private renderValidation(): JSX.Element | null {
    return (
      this.validation?.text !== undefined && <span class="status-text">{this.validation.text}</span>
    )
  }

  render(): JSX.Element {
    const buttonClasses = {
      [`toggle-button`]: true,
    }

    const commonAttributes = {
      disabled: this.disabled,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
    }

    const trueButtonAttributes = {
      ...commonAttributes,
      class: {
        ...buttonClasses,
        [`toggle-button-selected`]: this.value === true,
      },
      onClick: this.setTrue,
    }

    const falseButtonAttributes = {
      ...commonAttributes,
      class: {
        ...buttonClasses,
        [`toggle-button-selected`]: this.value === false,
      },
      onClick: this.setFalse,
    }

    const toggleClasses = {
      [`toggle`]: true,
      [`${this.validation?.status}`]: this.validation?.status !== undefined,
    }

    return (
      <div class={toggleClasses}>
        {this.renderLabel()}
        <div class="toggle-wrapper">
          <button {...falseButtonAttributes}>{this.falseLabel}</button>
          <button {...trueButtonAttributes}>{this.trueLabel}</button>
        </div>
        {this.renderValidation()}
      </div>
    )
  }
}
